import { Injectable, computed, inject, signal } from '@angular/core';
import { AdminPermission, AdminPermissionPreset, AdminPermissionPresetPayload } from 'mbs-ui-shared@models/AdminPermissionPreset';
import { PermissionsEnum } from 'mbs-ui-shared@models/PermissionsEnum';
import { AdminPermissionPresetsService } from 'mbs-ui-shared@services/admin-permission-presets.service';
import { Observable, forkJoin, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AdminPermissionPresetsFacade {
  private presetsService = inject(AdminPermissionPresetsService);

  public readonly presets = signal<AdminPermissionPreset[]>([]);
  public readonly permissions = signal<AdminPermission[]>([]);
  public readonly loading = signal(false);

  public readonly permissionLabels = computed(() =>
    this.permissions().reduce<Partial<Record<PermissionsEnum, string>>>(
      (acc, { name, displayName }) => ({ ...acc, [name]: displayName }),
      {}
    )
  );

  // Both lists are only needed once the permissions tab is rendered, so they are fetched on first use
  public init(): Observable<unknown> {
    if (this.permissions().length) return of(null);

    this.loading.set(true);

    return forkJoin([this.presetsService.get(), this.presetsService.getAvailablePermissions()]).pipe(
      tap({
        next: ([presets, permissions]) => {
          this.presets.set(presets ?? []);
          this.permissions.set(permissions ?? []);
        },
        finalize: () => this.loading.set(false)
      })
    );
  }

  public load(): Observable<AdminPermissionPreset[]> {
    this.loading.set(true);

    return this.presetsService.get().pipe(
      tap({
        next: (presets) => this.presets.set(presets ?? []),
        finalize: () => this.loading.set(false)
      })
    );
  }

  public create(preset: AdminPermissionPresetPayload): Observable<AdminPermissionPreset> {
    return this.presetsService.create(preset).pipe(tap(() => this.reload()));
  }

  public update(presetId: string, preset: AdminPermissionPresetPayload): Observable<AdminPermissionPreset> {
    return this.presetsService.update(presetId, preset).pipe(tap(() => this.reload()));
  }

  public delete(presetId: string): Observable<null> {
    return this.presetsService.delete(presetId).pipe(tap(() => this.reload()));
  }

  private reload(): void {
    this.load().subscribe();
  }
}
